import React, { useState } from 'react';
import { Building2, MapPin, DollarSign, Search, Filter, Users, Calendar } from 'lucide-react';
import EmptyState from '../../../components/ui/EmptyState';

const PropertyList = ({ properties, loading, commune, onPropertyUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Filter properties
  const filteredProperties = properties?.filter(property => {
    const matchesSearch = property?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) || 
                         property?.address?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         property?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = !statusFilter || property?.status === statusFilter;
    const matchesType = !typeFilter || property?.property_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Get unique values for filters
  const statuses = [...new Set(properties?.map(p => p?.status)?.filter(Boolean))];
  const types = [...new Set(properties?.map(p => p?.property_type)?.filter(Boolean))];

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'reserved': return 'bg-yellow-100 text-yellow-800';
      case 'sold': return 'bg-blue-100 text-blue-800';
      case 'withdrawn': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return 'Disponible';
      case 'reserved': return 'Réservé';
      case 'sold': return 'Vendu';
      case 'withdrawn': return 'Retiré';
      default: return status;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'apartment': return 'Appartement';
      case 'house': return 'Maison';
      case 'land': return 'Terrain';
      case 'commercial': return 'Commercial';
      case 'villa': return 'Villa';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 rounded w-1/4 mb-4"></div>
          {[...Array(3)]?.map((_, i) => (
            <div key={i} className="border-b last:border-b-0 pb-4 mb-4 last:mb-0">
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-300 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-blue-600" />
            Propriétés associées ({filteredProperties?.length})
          </h3>
        </div>

        {/* Search and Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Tous les statuts</option>
            {statuses?.map(status => (
              <option key={status} value={status}>{getStatusLabel(status)}</option>
            ))}
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Tous les types</option>
            {types?.map(type => (
              <option key={type} value={type}>{getTypeLabel(type)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="divide-y">
        {filteredProperties?.length > 0 ? (
          filteredProperties?.map(property => (
            <div key={property?.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-medium text-gray-900">{property?.title}</h4>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(property?.status)}`}>
                        {getStatusLabel(property?.status)}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getTypeLabel(property?.property_type)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center text-sm text-gray-600 mb-2">
                    <MapPin className="h-4 w-4 mr-1" />
                    {property?.address}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <DollarSign className="h-4 w-4 mr-1" />
                        {parseFloat(property?.price)?.toLocaleString()} FCFA
                      </span>
                      {property?.bedrooms && (
                        <span>{property?.bedrooms} chambres</span>
                      )}
                      {property?.bathrooms && (
                        <span>{property?.bathrooms} salles de bain</span>
                      )}
                      {property?.surface_area && (
                        <span>{property?.surface_area} m²</span>
                      )}
                    </div>

                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {property?.agent && (
                        <span className="flex items-center">
                          <Users className="h-4 w-4 mr-1" />
                          {property?.agent?.full_name}
                        </span>
                      )}
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(property?.created_at)?.toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>

                  {property?.description && (
                    <p className="mt-2 text-sm text-gray-700 line-clamp-2">{property?.description}</p>
                  )}

                  {property?.features && Object.keys(property?.features)?.length > 0 && (
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(property?.features)?.slice(0, 5)?.map(([key, value]) => {
                          if (value === true) {
                            return (
                              <span key={key} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                {key?.replace('_', ' ')?.toLowerCase()}
                              </span>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <EmptyState
            iconName="Building2"
            title="Aucune propriété"
            description={
              commune
                ? `Aucune propriété n'est associée à la commune ${commune?.name}`
                : 'Sélectionnez une commune pour voir ses propriétés'
            }
          />
        )}
      </div>
    </div>
  );
};

export default PropertyList;